import type { PlaceGeoJSON } from "./api";

interface LandingPageData {
  name: string;
  description: string | null;
  publishedAt?: Date;
  updatedAt?: Date;
  curatedBy?: { displayName: string } | null;
}

interface LandingPageJsonLdParams {
  map: LandingPageData;
  places: PlaceGeoJSON;
  canonicalUrl: string;
}

/**
 * Builds JSON-LD structured data for a landing page.
 * Includes Organization, WebSite, WebPage, TouristMap with places, and BreadcrumbList.
 */
export function buildLandingPageJsonLd({ map, places, canonicalUrl }: LandingPageJsonLdParams): object {
  const { name, description, curatedBy } = map;

  const placeItems = places.features.map((feature, index) => {
    const props = feature.properties;
    const [lng, lat] = feature.geometry.coordinates;
    const image = props.images[0]?.large;

    return {
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Place",
        "@id": `${canonicalUrl}#place-${feature.id}`,
        name: props.name,
        description: props.description || undefined,
        geo: {
          "@type": "GeoCoordinates",
          latitude: lat,
          longitude: lng,
        },
        ...(image && { image }),
      },
    };
  });

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://noma.to/#organization",
        name: "noma.to",
        url: "https://noma.to",
        description: "Curated outdoor adventures off the beaten path",
      },
      {
        "@type": "WebSite",
        "@id": "https://noma.to/#website",
        url: "https://noma.to",
        name: "noma.to",
        publisher: { "@id": "https://noma.to/#organization" },
      },
      {
        "@type": "WebPage",
        "@id": canonicalUrl,
        url: canonicalUrl,
        name,
        description: description || `Explore ${name} - curated outdoor adventures`,
        isPartOf: { "@id": "https://noma.to/#website" },
        ...(curatedBy?.displayName && {
          author: { "@type": "Person", name: curatedBy.displayName },
        }),
        ...(map.publishedAt && {
          datePublished: map.publishedAt.toISOString(),
        }),
        ...(map.updatedAt && {
          dateModified: map.updatedAt.toISOString(),
        }),
      },
      {
        "@type": "TouristMap",
        "@id": `${canonicalUrl}#map`,
        name,
        description: description || undefined,
        url: canonicalUrl,
        ...(curatedBy?.displayName && {
          author: { "@type": "Person", name: curatedBy.displayName },
        }),
        mainEntity: {
          "@type": "ItemList",
          name: `Places in ${name}`,
          numberOfItems: places.features.length,
          itemListElement: placeItems,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "noma.to", item: "https://noma.to" },
          { "@type": "ListItem", position: 2, name, item: canonicalUrl },
        ],
      },
    ],
  };
}
